import {
  ArrowRight,
  Banknote,
  BarChart3,
  Boxes,
  Clock,
  Globe,
  HandCoins,
  Package,
  ShieldCheck,
  Smartphone,
  Store,
} from 'lucide-react';
import { Link } from '@tanstack/react-router';
import { useCurrentReseller } from '../../api/reseller';

/**
 * Reseller-branded landing — §5.2 / §5.3
 * Uses currentReseller for branding context, "Powered by Possignin" footnote.
 */
export function Landing() {
  const { data: r } = useCurrentReseller();
  if (!r) return null;
  const accent = r.primaryColor;

  return (
    <div className="min-h-screen bg-marketing dark:bg-marketing-dark">
      {/* Header */}
      <header className="px-4 sm:px-8 py-5 flex items-center justify-between border-b border-zinc-200 dark:border-zinc-700">
        <div className="flex items-center gap-2.5">
          <span
            className="w-9 h-9 rounded-lg grid place-items-center text-white font-semibold text-base shadow-sm"
            style={{ backgroundColor: accent }}
            aria-hidden="true"
          >
            {r.displayName.charAt(0)}
          </span>
          <span className="font-semibold text-base tracking-tight text-zinc-950 dark:text-zinc-50">
            {r.displayName}
          </span>
        </div>

        <nav className="hidden sm:flex items-center gap-7 text-sm">
          <a
            href="#features"
            className="text-zinc-600 dark:text-zinc-300 hover:text-zinc-900 dark:hover:text-zinc-50 transition"
          >
            Features
          </a>
          <Link
            to="/pricing"
            className="text-zinc-600 dark:text-zinc-300 hover:text-zinc-900 dark:hover:text-zinc-50 transition"
          >
            Pricing
          </Link>
          <a
            href={`mailto:${r.supportEmail}`}
            className="text-zinc-600 dark:text-zinc-300 hover:text-zinc-900 dark:hover:text-zinc-50 transition"
          >
            Contact
          </a>
        </nav>

        <div className="flex items-center gap-3">
          <Link
            to="/login"
            className="hidden sm:inline-block text-sm font-medium text-zinc-700 dark:text-zinc-200 hover:text-zinc-950 dark:hover:text-zinc-50"
          >
            Login
          </Link>
          <Link
            to="/signup"
            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-md text-sm font-medium text-white shadow-xs transition hover:opacity-90"
            style={{ backgroundColor: accent }}
          >
            Sign up
            <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="glow-bg absolute inset-0 pointer-events-none" aria-hidden="true" />

        <div className="relative px-4 sm:px-6 py-16 sm:py-24 max-w-6xl mx-auto text-center">
          <span
            className="inline-block px-3 py-1 rounded-full text-xs font-medium mb-6"
            style={{
              backgroundColor: `${accent}1a`,
              color: accent,
            }}
          >
            Built for Bangladesh
          </span>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-zinc-950 dark:text-zinc-50 leading-[1.05] max-w-4xl mx-auto">
            Run your shop with {r.displayName}
            <br />
            <span className="text-zinc-500 dark:text-zinc-500">
              — built for Bangladesh.
            </span>
          </h1>

          <p className="mt-6 text-base sm:text-lg text-zinc-600 dark:text-zinc-300 max-w-2xl mx-auto">
            Cloud POS with HRM, CRM, manufacturing, repair, and WooCommerce sync. Pay in BDT,
            get cash or SSLCommerz. Built for shops, restaurants, pharmacies, and workshops.
          </p>

          <div className="mt-9 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to="/signup"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md text-base font-medium text-white shadow-sm transition hover:opacity-90 w-full sm:w-auto"
              style={{ backgroundColor: accent }}
            >
              Start free trial
              <ArrowRight className="w-4 h-4" />
            </Link>
            <a
              href={`mailto:${r.supportEmail}`}
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md text-base font-medium text-zinc-900 dark:text-zinc-50 border border-zinc-300 dark:border-zinc-700 bg-white/80 dark:bg-zinc-900/60 hover:bg-white dark:hover:bg-zinc-900 transition w-full sm:w-auto backdrop-blur-sm"
            >
              Talk to {r.displayName}
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>

          {/* Secondary CTA — partner program door */}
          <div className="mt-5 flex justify-center">
            <Link
              to="/become-a-reseller"
              className="group inline-flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-3 px-5 py-2.5 rounded-md border border-dashed border-zinc-300 dark:border-zinc-700 bg-transparent hover:border-zinc-400 dark:hover:border-zinc-500 transition text-center sm:text-left"
            >
              <span className="inline-flex items-center gap-1.5 text-sm font-medium text-zinc-900 dark:text-zinc-50">
                Become a reseller
                <ArrowRight className="w-3.5 h-3.5 transition group-hover:translate-x-0.5" />
              </span>
              <span className="text-xs text-zinc-500 dark:text-zinc-400">
                Earn commission selling Possignin under your brand.
              </span>
            </Link>
          </div>

          <ul className="mt-10 flex flex-wrap items-center justify-center gap-x-6 gap-y-3 text-sm text-zinc-600 dark:text-zinc-300">
            <Tick>7-day trial</Tick>
            <Tick>Cash to office in Sylhet</Tick>
            <Tick>Setup in 5 min</Tick>
            <Tick>Bangla + English UI</Tick>
          </ul>
        </div>
      </section>

      {/* Features */}
      <section
        id="features"
        className="px-4 sm:px-6 py-16 sm:py-20 max-w-6xl mx-auto"
      >
        <div className="text-center mb-12">
          <p className="text-xs font-medium uppercase tracking-wider text-zinc-500 dark:text-zinc-400">
            Why {r.displayName}
          </p>
          <h2 className="mt-2 text-3xl sm:text-4xl font-semibold tracking-tight text-zinc-950 dark:text-zinc-50">
            Everything your shop needs.
          </h2>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
          <Feature
            icon={Boxes}
            accent={accent}
            title="Sales & inventory"
            body="Track every sale and every item. FIFO, lot tracking, and barcode scanning out of the box."
          />
          <Feature
            icon={HandCoins}
            accent={accent}
            title="BDT-native billing"
            body="SSLCommerz built in. Cash to office. Auto invoices and receipts in BDT."
          />
          <Feature
            icon={BarChart3}
            accent={accent}
            title="Reports that read"
            body="Daily sales, P&L, stock alerts. Export to CSV any time."
          />
          <Feature
            icon={Smartphone}
            accent={accent}
            title="Works on a phone"
            body="POS that runs on Android, iPhone, or any browser. Receipt printers supported."
          />
          <Feature
            icon={Globe}
            accent={accent}
            title="Bangla + English"
            body="Switch language per user. Receipts and reports follow."
          />
          <Feature
            icon={ShieldCheck}
            accent={accent}
            title="Calm and secure"
            body="2FA, audit log, role-based access. Your data stays in your database — we never sell it."
          />
        </div>
      </section>

      {/* For partners — secondary door to the reseller program */}
      <section
        id="partners"
        className="px-4 sm:px-6 py-16 sm:py-20 max-w-6xl mx-auto border-t border-zinc-200 dark:border-zinc-800"
      >
        <div className="text-center mb-10">
          <p className="text-xs font-medium uppercase tracking-wider text-zinc-500 dark:text-zinc-400">
            For partners
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-start">
          <div>
            <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight text-zinc-950 dark:text-zinc-50 leading-tight">
              Sell Possignin under your own brand
            </h2>
            <p className="mt-4 text-base text-zinc-600 dark:text-zinc-300 leading-relaxed">
              Become a reseller and earn 5–50% commission on every business that signs up
              under your subdomain. Set your own packages. Use our infrastructure.
            </p>

            <div className="mt-7 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              <Link
                to="/become-a-reseller"
                className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-md text-sm font-medium text-white shadow-sm transition hover:opacity-90"
                style={{ backgroundColor: accent }}
              >
                Apply now
                <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                href="#partner-faq"
                className="inline-flex items-center justify-center gap-1 text-sm font-medium text-zinc-700 dark:text-zinc-200 hover:text-zinc-950 dark:hover:text-zinc-50 transition"
              >
                How does it work?
                <span aria-hidden="true">→</span>
              </a>
            </div>
          </div>

          <ul className="space-y-5">
            <PartnerBullet
              icon={Store}
              accent={accent}
              title="Your subdomain, your branding"
            />
            <PartnerBullet
              icon={Package}
              accent={accent}
              title="Build your own packages and pricing"
            />
            <PartnerBullet
              icon={Banknote}
              accent={accent}
              title="Variable commission, paid every 15 or 30 days"
            />
          </ul>
        </div>

        {/* Partner FAQ */}
        <div id="partner-faq" className="mt-14 sm:mt-16 max-w-3xl mx-auto">
          <h3 className="text-center text-xs font-medium uppercase tracking-wider text-zinc-500 dark:text-zinc-400 mb-6">
            Reseller FAQ
          </h3>
          <dl className="divide-y divide-zinc-200 dark:divide-zinc-800 rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950">
            <FaqRow
              q="Do I need to write any code?"
              a="No. Approved resellers get a branded subdomain and an admin panel."
            />
            <FaqRow
              q="How long does approval take?"
              a="Most applications are reviewed within 1–2 business days."
            />
            <FaqRow
              q="What happens if I'm rejected?"
              a="We'll explain why. You can re-apply after a cooldown period."
            />
          </dl>
        </div>
      </section>

      {/* CTA strip */}
      <section className="px-4 sm:px-6 py-14 max-w-6xl mx-auto">
        <div className="rounded-2xl border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-950 p-8 sm:p-12 text-center">
          <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-zinc-950 dark:text-zinc-50">
            Ready to get started?
          </h2>
          <p className="mt-2 text-sm text-zinc-600 dark:text-zinc-300 max-w-md mx-auto">
            Free for 7 days, no card needed. Setup takes 5 minutes.
          </p>
          <Link
            to="/signup"
            className="mt-6 inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md text-base font-medium text-white shadow-sm transition hover:opacity-90"
            style={{ backgroundColor: accent }}
          >
            Start free trial
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="px-4 sm:px-8 py-8 border-t border-zinc-200 dark:border-zinc-700">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-zinc-500 dark:text-zinc-400 flex items-center gap-1.5">
            <Clock className="w-3.5 h-3.5" />
            Powered by{' '}
            <a
              href="https://saas.itcitysoft.com"
              className="font-semibold text-zinc-700 dark:text-zinc-200 hover:underline"
            >
              Possignin
            </a>
          </p>
          <div className="flex items-center gap-4 text-xs text-zinc-500 dark:text-zinc-400">
            <a href="https://status.itcitysoft.com" target="_blank" rel="noreferrer" className="hover:text-zinc-700 dark:hover:text-zinc-200">
              Status
            </a>
            <a href="https://itcitysoft.com/terms" target="_blank" rel="noreferrer" className="hover:text-zinc-700 dark:hover:text-zinc-200">
              Terms
            </a>
            <a href="https://itcitysoft.com/privacy" target="_blank" rel="noreferrer" className="hover:text-zinc-700 dark:hover:text-zinc-200">
              Privacy
            </a>
            <a
              href={`mailto:${r.supportEmail}`}
              className="hover:text-zinc-700 dark:hover:text-zinc-200 mono"
            >
              {r.supportEmail}
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}

function Tick({ children }: { children: React.ReactNode }) {
  return (
    <li className="flex items-center gap-1.5">
      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" aria-hidden="true" />
      {children}
    </li>
  );
}

function Feature({
  icon: Icon,
  accent,
  title,
  body,
}: {
  icon: React.ComponentType<{ className?: string }>;
  accent: string;
  title: string;
  body: string;
}) {
  return (
    <div className="rounded-xl border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-950 p-5 sm:p-6 hover:shadow-sm transition">
      <span
        className="w-10 h-10 grid place-items-center rounded-lg text-white"
        style={{ backgroundColor: accent }}
      >
        <Icon className="w-5 h-5" />
      </span>
      <h3 className="mt-4 text-base font-semibold text-zinc-950 dark:text-zinc-50">
        {title}
      </h3>
      <p className="mt-1.5 text-sm text-zinc-600 dark:text-zinc-300 leading-relaxed">
        {body}
      </p>
    </div>
  );
}

function PartnerBullet({
  icon: Icon,
  accent,
  title,
}: {
  icon: React.ComponentType<{ className?: string }>;
  accent: string;
  title: string;
}) {
  return (
    <li className="flex items-start gap-3.5">
      <span
        className="shrink-0 w-9 h-9 grid place-items-center rounded-lg text-white"
        style={{ backgroundColor: accent }}
        aria-hidden="true"
      >
        <Icon className="w-4 h-4" />
      </span>
      <span className="pt-1.5 text-sm sm:text-base font-medium text-zinc-900 dark:text-zinc-100">
        {title}
      </span>
    </li>
  );
}

function FaqRow({ q, a }: { q: string; a: string }) {
  return (
    <div className="px-5 py-4 sm:px-6 sm:py-5">
      <dt className="text-sm font-semibold text-zinc-950 dark:text-zinc-50">{q}</dt>
      <dd className="mt-1.5 text-sm text-zinc-600 dark:text-zinc-300 leading-relaxed">
        {a}
      </dd>
    </div>
  );
}
